/**
 * Iframe PostMessage Utilities
 *
 * Handles communication between embedded donation form and parent window
 */

export interface DonationMessage {
  source: 'giftaid-donation';
  type:
    | 'donation_success'
    | 'donation_cancelled'
    | 'donation_error'
    | 'donation_height'
    | 'donation_ready'
    | 'donation_redirect'
    | 'donation_redirect_starting'
    | 'donation_return';
  donationId?: string;
  amount?: number;
  error?: string;
  height?: number;
  url?: string;
  status?: 'succeeded' | 'failed' | 'pending';
}

/**
 * Send message to parent window if in iframe
 */
export function postMessageToParent(
  message: Omit<DonationMessage, 'source'>
): void {
  if (typeof window === 'undefined') return;

  // Check if we're in an iframe
  if (window.parent === window) return;

  try {
    // Use '*' for targetOrigin since we don't know parent origin
    // The parent should validate the message source
    window.parent.postMessage({ source: 'giftaid-donation', ...message }, '*');
  } catch (error) {
    console.error('Failed to post message to parent:', error);
  }
}

/**
 * Check if running in iframe embed mode
 */
export function isEmbedMode(): boolean {
  if (typeof window === 'undefined') return false;

  const url = new URL(window.location.href);
  return url.searchParams.get('embed') === 'true';
}

/**
 * Check if we're inside an iframe
 */
export function isInIframe(): boolean {
  if (typeof window === 'undefined') return false;
  return window.parent !== window;
}

/**
 * Report current height to parent for auto-resize
 */
export function reportHeight(): void {
  if (!isInIframe()) return;

  const height = document.documentElement.scrollHeight;
  postMessageToParent({ type: 'donation_height', height });
}
